import { postPrepareSimpleUpload } from '@rendora/common/entities/upload_media_v3';
import type OSS from 'ali-oss';

export interface IUploadOptions {
  auth: string;
  is_transcoding?: boolean;
  ignore_query_status?: boolean;
}

// OSS上传服务类
// 提供文件上传到阿里云OSS的功能，支持简单上传
export class OSSUpload {
  // ==================== 公共属性 ====================
  public state: 'idle' | 'uploading' | 'completed' | 'failed' | 'cancelled' = 'idle';
  public file!: File;
  public uid = ''; // 上传文件唯一标识

  // ==================== 私有属性 ====================
  private auth!: string; // user_id
  private client!: OSS;
  private is_transcoding = false; // 默认资源不转码

  // ==================== 上传上下文 ====================
  private uploadContext = {
    name: '', // 上传文件路径
  };

  // ==================== 构造函数 ====================
  constructor(options: IUploadOptions) {
    const { auth, is_transcoding } = options;

    if (!options.auth) {
      throw new Error('auth is required');
    }

    this.auth = auth;

    if (is_transcoding !== undefined) {
      this.is_transcoding = is_transcoding;
    }
  }

  // ==================== 公共方法 ====================

  /**
   * 执行简单上传
   */
  async simpleUpload(file: File & { uid: string }) {
    if (!file) {
      return;
    }

    this.state = 'uploading';
    this.uid = file.uid || `uid-${+Date.now()}`;
    this.file = file;

    const { name: filename, size: filesize } = this.file;

    try {
      const res = await postPrepareSimpleUpload({
        auth_type: 'sts',
        filename,
        filesize,
      });

      const { upload_path } = res;
      this.uploadContext.name = upload_path;

      const client = await this.createOssClient({
        accessKeyId: res.credential.access_key_id,
        accessKeySecret: res.credential.access_key_secret,
        bucket: res.credential.bucket,
        region: res.credential.region,
        stsToken: res.credential.security_token,
        refreshSTSToken() {
          return Promise.resolve({
            accessKeyId: res.credential.access_key_id,
            accessKeySecret: res.credential.access_key_secret,
            stsToken: res.credential.security_token,
          });
        },
      });

      this.client = client;

      const options = this.createUploadOptions();

      try {
        await client.multipartUpload(upload_path, this.file, options);
        this.completeUpload(res as any);
      } catch (err) {
        this.handleFail(JSON.stringify(err));
      }
    } catch (err) {
      this.handleFail(typeof err === 'string' ? err : JSON.stringify(err));
    }
  }

  /**
   * 取消上传
   */
  cancel() {
    this.state = 'cancelled';
    // 简单上传无法取消，只能标记状态
  }

  // ==================== 私有方法 ====================

  /**
   * 创建OSS客户端实例
   */
  private async createOssClient(option: OSS.Options) {
    const OssClient = await import('ali-oss').then(m => m.default);
    return new OssClient(option);
  }

  /**
   * 创建上传选项
   */
  private createUploadOptions() {
    return {
      progress: (p: number) => {
        // 简单上传的进度处理
        if (p === 1) {
          // 上传完成
        }
      },
      // 设置并发上传的分片数量为1（简单上传）
      parallel: 1,
      // 设置分片大小
      partSize: 1024 * 1024,
    };
  }

  // ==================== 工具方法 ====================

  /**
   * 处理失败
   */
  private handleFail(msg: string, info = {}) {
    this.state = 'failed';
  }

  /**
   * 处理成功
   */
  private handleSuccess() {
    this.state = 'completed';
  }

  /**
   * 完成上传
   */
  private completeUpload(media: any) {
    this.handleSuccess();
  }
}
